/**
 * Account Security Dashboard Page
 * Shows login history, active sessions, password status, and MFA status
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Shield, ArrowLeft, RefreshCw, Loader2, X,
  CheckCircle, AlertCircle, Activity, ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import logger from '../utils/logger';
import {
  generateMockSecurityData, generateMockLoginHistory,
  generateMockSessions, getSecurityScore
} from '../components/security/securityMockData';
import SecurityOverviewTab from '../components/security/SecurityOverviewTab';
import TrustedDevicesTab from '../components/security/TrustedDevicesTab';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AccountSecurityDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [securityData, setSecurityData] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [terminatingSession, setTerminatingSession] = useState(null);
  const [removingDevice, setRemovingDevice] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchSecurityData();
    fetchTrustedDevices();
  }, []);

  const fetchSecurityData = async () => {
    setLoading(true);
    try {
      const securityRes = await fetch(`${API_URL}/api/auth/security-overview`, {
        credentials: 'include'
      });
      if (securityRes.ok) {
        const data = await securityRes.json();
        setSecurityData(data);
        setLoginHistory(data.login_history || []);
        setActiveSessions(data.active_sessions || []);
      } else {
        setSecurityData(generateMockSecurityData(user));
        setLoginHistory(generateMockLoginHistory());
        setActiveSessions(generateMockSessions());
      }
    } catch (err) {
      logger.error('Failed to fetch security data:', err);
      setSecurityData(generateMockSecurityData(user));
      setLoginHistory(generateMockLoginHistory());
      setActiveSessions(generateMockSessions());
    }
    setLoading(false);
  };

  const fetchTrustedDevices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/known-locations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTrustedDevices(data.known_locations || []);
      }
    } catch (err) {
      logger.error('Failed to fetch trusted devices:', err);
    }
  };

  const removeTrustedDevice = async (deviceId) => {
    setRemovingDevice(deviceId);
    try {
      await fetch(`${API_URL}/api/auth/known-locations/${deviceId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
    } catch (err) {
      logger.error('Failed to remove device:', err);
    }
    setTrustedDevices(prev => prev.filter(d => d.id !== deviceId));
    setSuccess('Trusted device removed');
    setRemovingDevice(null);
    setTimeout(() => setSuccess(''), 3000);
  };

  const clearAllTrustedDevices = async () => {
    try {
      await fetch(`${API_URL}/api/auth/known-locations/clear-all`, {
        method: 'DELETE',
        credentials: 'include'
      });
    } catch (err) {
      logger.error('Failed to clear devices:', err);
    }
    setTrustedDevices([]);
    setSuccess('All trusted devices cleared. You will receive alerts for all future logins.');
    setTimeout(() => setSuccess(''), 4000);
  };

  const terminateSession = async (sessionId) => {
    if (sessionId === 'current') {
      setError("You can't terminate your current session");
      return;
    }
    setTerminatingSession(sessionId);
    try {
      await fetch(`${API_URL}/api/auth/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
    } catch (err) {
      logger.error('Failed to terminate session:', err);
    }
    setActiveSessions(prev => prev.filter(s => s.id !== sessionId));
    setSuccess('Session terminated successfully');
    setTerminatingSession(null);
    setTimeout(() => setSuccess(''), 3000);
  };

  const terminateAllSessions = async () => {
    try {
      await fetch(`${API_URL}/api/auth/sessions/terminate-all`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      logger.error('Failed to terminate all sessions:', err);
    }
    setActiveSessions(prev => prev.filter(s => s.is_current));
    setSuccess('All other sessions terminated');
    setTimeout(() => setSuccess(''), 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const securityScore = getSecurityScore(securityData);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50" data-testid="security-dashboard">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10 border-b border-orange-100">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-orange-50 rounded-full transition"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-orange-500" />
                  Security Dashboard
                </h1>
                <p className="text-sm text-gray-500">Monitor your account security</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => { fetchSecurityData(); fetchTrustedDevices(); }}
              className="border-orange-200 hover:bg-orange-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 mt-4 border-b border-orange-100 -mb-4">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition ${
                activeTab === 'overview'
                  ? 'bg-orange-50 text-orange-700 border-b-2 border-orange-500'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="tab-overview"
            >
              <Activity className="w-4 h-4 inline mr-2" />
              Security Overview
            </button>
            <button
              onClick={() => setActiveTab('trusted')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition ${
                activeTab === 'trusted'
                  ? 'bg-orange-50 text-orange-700 border-b-2 border-orange-500'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="tab-trusted-devices"
            >
              <ShieldCheck className="w-4 h-4 inline mr-2" />
              Trusted Devices
              {trustedDevices.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-orange-100 text-orange-700 rounded-full">
                  {trustedDevices.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        )}

        {activeTab === 'trusted' && (
          <TrustedDevicesTab
            trustedDevices={trustedDevices}
            removingDevice={removingDevice}
            onRemoveDevice={removeTrustedDevice}
            onClearAll={clearAllTrustedDevices}
          />
        )}

        {activeTab === 'overview' && (
          <SecurityOverviewTab
            securityData={securityData}
            securityScore={securityScore}
            activeSessions={activeSessions}
            loginHistory={loginHistory}
            terminatingSession={terminatingSession}
            onTerminateSession={terminateSession}
            onTerminateAll={terminateAllSessions}
            user={user}
          />
        )}
      </main>
    </div>
  );
};

export default AccountSecurityDashboard;
